/** Header (3.1): back button, d-m title that truncates before the actions, up to two round actions. */
import type { ReactElement, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoundButton, type RoundButtonProps } from "./RoundButton";
import { Icon } from "./Icon";

export interface HeaderProps { title?: ReactNode; back?: boolean | string; /** D-215: replaces the back step (where to go is worked out at the tap). */ onBack?: () => void; actions?: RoundButtonProps[]; subtitle?: ReactNode; leading?: ReactNode; /** D-160: after the actions (the balance chip, the bell). */ trailing?: ReactNode; className?: string }
export function Header({ title, back = false, onBack, actions = [], subtitle, leading, trailing, className = "" }: HeaderProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  // D-171: back returns to the page you came from. Only a page opened cold (a shared link, a reload: React Router's
  // "default" key) goes to its parent (`back` as a path) or Home, never out of the app.
  const goBack = () => { if (onBack) onBack(); else if (location.key !== "default") navigate(-1); else navigate(typeof back === "string" ? back : "/"); };
  return (
    <>
      <header className={`header ${className}`}>
        {back && <RoundButton icon="chevron-left" label="Back" onClick={goBack} />}
        {leading}
        {/* A long name (a tournament, a club) steps down a size so it fits in two lines (D-072). */}
        {title !== undefined && <h1 className={`header__title ${typeof title === "string" && title.length > 22 ? "header__title--long" : ""}`}>{title}</h1>}
        {actions.length > 0 || trailing ? <div className="header__actions">{actions.slice(0, 2).map((a) => <RoundButton key={a.icon + a.label} {...a} />)}{trailing}</div> : null}
      </header>
      {subtitle && <p className="header__subtitle">{subtitle}</p>}
    </>
  );
}

/** D-201: the DIGU Classic logo in the bars: the outlined club in lime, then DIGU over a tracked-out CLASSIC. */
export function Lockup({ size = 44 }: { size?: number }): ReactElement {
  return (
    <span className="lockup" aria-label="DIGU Classic">
      <Icon name="club" size={Math.round(size * 0.92)} stroke={2.1} className="lockup__mark" />
      <span className="lockup__words" aria-hidden="true">
        <span className="lockup-word" style={{ fontSize: Math.round(size * 0.56), lineHeight: 1 }}>DIGU</span>
        <span className="lockup-sub" style={{ fontSize: Math.max(7, Math.round(size * 0.2)), lineHeight: 1 }}>CLASSIC</span>
      </span>
    </span>
  );
}

/** D-201: the app-icon mark: a Volt Lime rounded square with the club drawn in Midnight Green. Brand colours in every theme. */
export function BrandMark({ size = 52 }: { size?: number }): ReactElement {
  return (
    <span className="brandmark" style={{ width: size, height: size, borderRadius: Math.round(size * 0.28) }} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={Math.round(size * 0.68)} height={Math.round(size * 0.68)} fill="none" stroke="currentColor" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.00 14.28A4.2 4.2 0 1 1 7.88 8.80A4.2 4.2 0 1 1 16.12 8.80A4.2 4.2 0 1 1 12.00 14.28Z" />
        <path d="M12 14.6c0 2.4-.5 4.3-1.9 5.9h3.8c-1.4-1.6-1.9-3.5-1.9-5.9" />
      </svg>
    </span>
  );
}
